import React from 'react';
// Deep imports on purpose. The package roots (`html-react-parser`, `html-dom-parser`)
// map html-dom-parser's browser build through its `browser` field, and that build
// reads `window`/`document` at module load. The server SSR-loads the client
// webpack bundle, so it must only ever contain the htmlparser2 build.
import htmlToDOM from 'html-dom-parser/lib/server/html-to-dom';
import domToReact from 'html-react-parser/lib/dom-to-react';

/**
 * Renders author-written rich text (listing descriptions, package descriptions,
 * FAQ answers, rules) as React nodes through a tag allowlist, never as raw
 * innerHTML. The stored HTML is extended data any author can write through
 * the SDK, so the editor's own format allowlist can't be trusted here.
 *
 * - Allowed tags render with no attributes at all (no style, class, href, on*).
 * - Dangerous containers are dropped together with their content.
 * - Every other element is unwrapped: its children's text is kept.
 *
 * Parsing always uses htmlparser2 (no DOM, works in SSR), so nothing in the
 * input loads or runs while it is parsed.
 */

const PARSER_OPTIONS = { decodeEntities: true, lowerCaseAttributeNames: false };

const ALLOWED_TAGS = new Set([
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'blockquote',
  'ul',
  'ol',
  'li',
  'h2',
  'h3',
]);

const DROPPED_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'svg',
  'math',
  'template',
  'noscript',
]);

const VOID_TAGS = new Set(['br']);

// htmlparser2 types <script> and <style> separately from other tags.
const ELEMENT_TYPES = new Set(['tag', 'script', 'style']);

const removed = () => <React.Fragment />;

const options = {
  replace: node => {
    if (node.type === 'text') {
      // Default handling: a plain string, escaped by React.
      return undefined;
    }
    if (!ELEMENT_TYPES.has(node.type)) {
      // Comments, directives, CDATA.
      return removed();
    }

    const name = (node.name || '').toLowerCase();
    if (DROPPED_TAGS.has(name)) {
      return removed();
    }

    const children =
      node.children && node.children.length > 0 ? domToReact(node.children, options) : null;

    if (ALLOWED_TAGS.has(name)) {
      return VOID_TAGS.has(name)
        ? React.createElement(name)
        : React.createElement(name, null, children);
    }
    return <React.Fragment>{children}</React.Fragment>;
  },
};

export const renderRichText = html => {
  if (typeof html !== 'string') {
    return null;
  }
  return domToReact(htmlToDOM(html, PARSER_OPTIONS), options);
};
